import { useCallback, useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { Camera, ImageUp, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { fileSize } from '@/lib/format';
import { NOUN, VERB, plural, pluralize } from '@/lib/plural';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const MAX_FILE_BYTES = 8 * 1024 * 1024;

const ACCEPT_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

interface RejectedFile {
  name: string;
  reason: string;
}

/** Client-side gate so an 80 MB photo never starts a doomed upload. */
function validateImage(file: File): string | null {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return 'Нужно изображение JPEG, PNG, WebP или HEIC.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return `Слишком большой (${fileSize(file.size)}). Максимум — ${fileSize(MAX_FILE_BYTES)}.`;
  }
  if (file.size === 0) {
    return 'Файл пустой.';
  }
  return null;
}

/** Shared shell for the two file-picker labels, sized as a real touch target. */
const PICKER_LABEL =
  'focus-within:shadow-focus inline-flex h-11 w-full shrink-0 cursor-pointer items-center ' +
  'justify-center gap-2 rounded-md border border-line-strong bg-surface px-3.5 ' +
  'text-base font-medium text-fg transition-colors duration-fast ' +
  'hover:border-fg-faint hover:bg-sunken sm:w-auto lg:h-9';

export interface DropZoneProps {
  files: File[];
  onChange: (next: File[]) => void;
  maxFiles: number;
  label: string;
  /** Concrete requirements, e.g. "all four corners visible, no glare". */
  requirements?: string;
  disabled?: boolean;
  className?: string;
  /**
   * Adds a second control that opens the camera directly — `environment` for
   * the rear lens (a document on a table), `user` for the front one (a selfie).
   *
   * It is a *separate* input on purpose: `capture` on the only picker makes
   * several browsers skip the file browser entirely, and plenty of traders
   * photograph their banking app first and upload from the gallery afterwards.
   */
  capture?: 'user' | 'environment';
}

export function DropZone({
  files,
  onChange,
  maxFiles,
  label,
  requirements,
  disabled = false,
  className,
  capture,
}: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  // Accepted files are otherwise a purely visual change (a thumbnail appears);
  // this gives assistive tech the same confirmation.
  const [status, setStatus] = useState('');
  const inputId = useId();
  const cameraInputId = useId();
  const isFull = files.length >= maxFiles;

  // With a camera button beside it, the second control is "the other source";
  // on its own it is simply the picker.
  const galleryLabel = capture
    ? 'Из галереи'
    : maxFiles > 1
      ? 'Выбрать файлы'
      : 'Выбрать файл';

  const accept = useCallback(
    (incoming: FileList | null) => {
      if (!incoming || incoming.length === 0) return;

      const accepted: File[] = [];
      const refused: RejectedFile[] = [];
      let remaining = maxFiles - files.length;

      for (const file of Array.from(incoming)) {
        if (remaining <= 0) {
          refused.push({
            name: file.name,
            reason: `Можно прикрепить не больше ${pluralize(maxFiles, NOUN.file)}.`,
          });
          continue;
        }
        const problem = validateImage(file);
        if (problem) {
          refused.push({ name: file.name, reason: problem });
          continue;
        }
        accepted.push(file);
        remaining -= 1;
      }

      setRejected(refused);
      if (accepted.length > 0) {
        onChange([...files, ...accepted]);
        const total = files.length + accepted.length;
        setStatus(
          `${plural(accepted.length, VERB.added)} ${pluralize(accepted.length, NOUN.file)}. ` +
            `Прикреплено ${total} из ${maxFiles}.`,
        );
      } else if (refused.length > 0) {
        setStatus('');
      }
    },
    [files, maxFiles, onChange],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || isFull) return;
    accept(event.dataTransfer.files);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disabled || isFull) return;
    setIsDragging(true);
  };

  const remove = (index: number): void => {
    const removed = files[index];
    onChange(files.filter((_, position) => position !== index));
    setRejected([]);
    setStatus(removed ? `Файл ${removed.name} удалён.` : 'Файл удалён.');
    // The button that had focus is about to unmount; move focus somewhere
    // meaningful instead of letting it fall back to <body>.
    inputRef.current?.focus();
  };

  return (
    <div className={className}>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={() => setIsDragging(false)}
        className={cn(
          'rounded-md border border-dashed px-4 py-5 transition-colors duration-fast',
          isDragging ? 'border-accent-500 bg-accent-50' : 'border-line-strong bg-sunken',
          (disabled || isFull) && 'opacity-60',
        )}
      >
        <div className="flex flex-col items-start gap-2 text-left sm:flex-row sm:items-center sm:gap-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-line bg-surface text-fg-muted">
            <ImageUp size={16} strokeWidth={1.5} aria-hidden="true" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-base font-medium text-fg">{label}</p>
            {requirements ? (
              <p className="mt-0.5 text-sm leading-snug text-fg-secondary">{requirements}</p>
            ) : null}
            <p className="mt-1 text-xs text-fg-secondary">
              {capture ? 'Снимите камерой или выберите' : 'Перетащите'}{' '}
              {maxFiles > 1 ? 'файлы' : 'файл'}
              {capture ? '' : ' сюда или выберите вручную'}. JPEG, PNG, WebP или HEIC, до{' '}
              {fileSize(MAX_FILE_BYTES)} каждый. Добавлено {files.length} из {maxFiles}.
            </p>
          </div>

          <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            {capture ? (
              <label
                htmlFor={cameraInputId}
                className={cn(PICKER_LABEL, (disabled || isFull) && 'pointer-events-none opacity-50')}
              >
                <Camera size={15} strokeWidth={1.5} aria-hidden="true" />
                Снять камерой
                <input
                  id={cameraInputId}
                  type="file"
                  accept={ACCEPT_ATTR}
                  capture={capture}
                  // A camera returns one frame per tap; `multiple` would be
                  // ignored anyway, and the picker appends rather than replaces.
                  disabled={disabled || isFull}
                  onChange={(event) => {
                    accept(event.target.files);
                    event.target.value = '';
                  }}
                  className="sr-only"
                />
              </label>
            ) : null}

            <label
              htmlFor={inputId}
              className={cn(PICKER_LABEL, (disabled || isFull) && 'pointer-events-none opacity-50')}
            >
              <ImageUp size={15} strokeWidth={1.5} aria-hidden="true" className="sm:hidden" />
              {galleryLabel}
              <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept={ACCEPT_ATTR}
                multiple={maxFiles > 1}
                disabled={disabled || isFull}
                onChange={(event) => {
                  accept(event.target.files);
                  // Reset so re-selecting the same file still fires a change.
                  event.target.value = '';
                }}
                className="sr-only"
              />
            </label>
          </div>
        </div>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {status}
      </p>

      {rejected.length > 0 ? (
        <ul role="alert" className="mt-2 space-y-1">
          {rejected.map((entry) => (
            <li key={entry.name} className="text-xs text-danger-500">
              <span className="font-medium">{entry.name}</span> — {entry.reason}
            </li>
          ))}
        </ul>
      ) : null}

      {files.length > 0 ? (
        // A screenshot of a banking app is unreadable at 80px. Two columns on a
        // phone gives roughly 135px of portrait crop, which is enough to tell
        // one statement screenshot from another before sending it.
        <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {files.map((file, index) => (
            <Thumbnail
              // No index in the key: removing a file from the middle would
              // otherwise re-key every later thumbnail and churn their object
              // URLs.
              key={`${file.name}-${file.lastModified}-${file.size}`}
              file={file}
              onRemove={() => remove(index)}
              disabled={disabled}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Thumbnail({
  file,
  onRemove,
  disabled,
}: {
  file: File;
  onRemove: () => void;
  disabled: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);

  // Object URLs leak until revoked; tie the lifetime to the thumbnail.
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <li className="min-w-0">
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded-md border border-line bg-sunken">
        {url ? (
          <img
            src={url}
            alt={`Миниатюра файла ${file.name}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : null}

        {/* 44x44 and inside the frame rather than hanging off the corner: a
            negative-offset button on the first thumbnail of a row pushes the
            grid sideways on a narrow screen. */}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Удалить файл ${file.name}`}
          className={cn(
            'focus-ring absolute right-1 top-1 flex h-11 w-11 items-center justify-center',
            'rounded-full border border-line bg-surface text-fg-muted shadow-popover',
            'transition-colors duration-fast',
            'hover:border-danger-500 hover:text-danger-500 disabled:opacity-50',
          )}
        >
          <Trash2 size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </div>
      <p className="mt-1 truncate text-2xs text-fg-muted">
        <span className="sr-only">{file.name}, </span>
        {fileSize(file.size)}
      </p>
    </li>
  );
}
